/**
 * Gift Selection Store - Public API Layer
 *
 * This is the ONLY interface external code should use to access gift selection store.
 * Direct access to store observables or actions is FORBIDDEN.
 *
 * ARCHITECTURE PRINCIPLES:
 * - Encapsulation: Internal state hidden behind getters
 * - Single Responsibility: Each function has clear purpose
 * - Controlled Access: State mutations only through actions
 * - Type Safety: All functions properly typed
 * - Persistence: All changes automatically persisted via MMKV
 *
 * USAGE EXAMPLES:
 * ```typescript
 * // READ OPERATIONS
 * const session = giftSelectionStoreAPI.state.getSessionById('session-123');
 * const turns = giftSelectionStoreAPI.state.getSessionTurns('session-123');
 * const canAddMore = giftSelectionStoreAPI.computed.canAddMoreTurns('session-123');
 *
 * // WRITE OPERATIONS
 * const sessionId = giftSelectionStoreAPI.actions.createSession('product-123', 'product', gifts, 3);
 * giftSelectionStoreAPI.actions.addTurn(sessionId, newTurn);
 * giftSelectionStoreAPI.actions.deleteSession(sessionId);
 * ```
 */
import { type Observable } from '@legendapp/state';
import { giftSelectionStoreActions } from './simple-gift-selection-store.actions';
import {
    giftSelectionStore$,
    type GiftSelectionSession,
    type GiftSelectionEntityType,
    type GiftSelectionStoreState,
} from './simple-gift-selection.store';
import { type GiftSelectionTurn, type SelectedGift } from '../types/gift-selection.types';
import { type FreeGiftsData } from '../../../types/front.graphql';

// ============================================================================
// STATE GETTERS - READ-ONLY ACCESS
// ============================================================================

export const giftSelectionStoreStateAPI = {
    /**
     * Get all sessions
     * @returns Frozen object of all sessions
     */
    getAllSessions: (): Readonly<Record<string, GiftSelectionSession>> => {
        const sessions = giftSelectionStoreActions.getAllSessions();
        return Object.freeze(sessions);
    },

    /**
     * Get specific session by ID
     * @param sessionId - Session identifier
     * @returns Session data or undefined
     */
    getSessionById: (sessionId: string): Readonly<GiftSelectionSession> | undefined => {
        const session = giftSelectionStoreActions.getSessionById(sessionId);
        return session ? Object.freeze({ ...session }) : undefined;
    },

    /**
     * Get all sessions for a specific entity
     * @param entityId - Entity identifier (product ID or cart ID)
     * @param entityType - Type of entity
     * @returns Array of sessions
     */
    getSessionsByEntity: (
        entityId: string,
        entityType: GiftSelectionEntityType
    ): ReadonlyArray<GiftSelectionSession> => {
        const sessions = giftSelectionStoreActions.getSessionsByEntity(entityId, entityType);
        return Object.freeze(sessions.map(s => Object.freeze({ ...s })));
    },

    /**
     * Get most recent session for an entity
     * @param entityId - Entity identifier
     * @param entityType - Type of entity
     * @returns Most recent session or undefined
     */
    getMostRecentSession: (
        entityId: string,
        entityType: GiftSelectionEntityType
    ): Readonly<GiftSelectionSession> | undefined => {
        const session = giftSelectionStoreActions.getMostRecentSession(entityId, entityType);
        return session ? Object.freeze({ ...session }) : undefined;
    },

    /**
     * Get turns for a specific session
     * @param sessionId - Session identifier
     * @returns Array of turns
     */
    getSessionTurns: (sessionId: string): ReadonlyArray<GiftSelectionTurn> => {
        const turns = giftSelectionStoreActions.getSessionTurns(sessionId);
        return Object.freeze(turns.map(t => Object.freeze({ ...t })));
    },

    /**
     * Get available gifts for a session
     * @param sessionId - Session identifier
     * @returns Array of available gifts
     */
    getAvailableGifts: (sessionId: string): ReadonlyArray<FreeGiftsData> => {
        const gifts = giftSelectionStoreActions.getAvailableGifts(sessionId);
        return Object.freeze(gifts);
    },

    /**
     * Get selected gifts for a session
     * @param sessionId - Session identifier
     * @returns Array of selected gifts
     */
    getSelectedGifts: (sessionId: string): ReadonlyArray<SelectedGift> => {
        const gifts = giftSelectionStoreActions.getSelectedGifts(sessionId);
        return Object.freeze(gifts);
    },

    /**
     * Get product quantity for a session
     * @param sessionId - Session identifier
     * @returns Product quantity
     */
    getProductQuantity: (sessionId: string): number => {
        return giftSelectionStoreActions.getProductQuantity(sessionId);
    },

    /**
     * Get session observable for reactive updates (advanced use)
     * @param sessionId - Session identifier
     * @returns Observable of session
     */
    getSessionObservable: (sessionId: string): Observable<GiftSelectionSession> | undefined => {
        const session = giftSelectionStore$.sessions[sessionId]?.peek();
        return session ? giftSelectionStore$.sessions[sessionId] : undefined;
    },

    /**
     * Get all sessions observable (advanced use)
     * @returns Observable of all sessions
     */
    getAllSessionsObservable: (): Observable<Record<string, GiftSelectionSession>> => {
        return giftSelectionStore$.sessions;
    },

    /**
     * Get the root store observable
     * @returns Observable of the entire store state
     */
    getStoreObservable: (): Observable<GiftSelectionStoreState> => {
        return giftSelectionStore$;
    },

    /**
     * Get or create session observable for an entity
     * This is the recommended way to get session observable - abstracts away the key generation
     * @param entityId - Entity identifier (productId, cartItemReferenceId, etc.)
     * @param entityType - Type of entity ('product' or 'cart')
     * @returns Observable of session (creates new if doesn't exist)
     */
    getOrCreateSessionObservableByEntity: (
        entityId: string,
        entityType: GiftSelectionEntityType
    ): Observable<GiftSelectionSession> => {
        // Try to find existing session for this entity
        const existingSession = giftSelectionStoreActions.getMostRecentSession(entityId, entityType);

        if (existingSession) {
            // Return observable for existing session
            return giftSelectionStore$.sessions[existingSession.sessionId];
        }

        // Create new session and return its observable
        const newSessionId = giftSelectionStoreActions.createSession(entityId, entityType, [], 3);
        return giftSelectionStore$.sessions[newSessionId];
    },

    /**
     * Get session observable by entity (returns undefined if not exists)
     * Use this when you don't want to create a new session automatically
     * @param entityId - Entity identifier
     * @param entityType - Type of entity
     * @returns Observable of session or undefined
     */
    getSessionObservableByEntity: (
        entityId: string,
        entityType: GiftSelectionEntityType
    ): Observable<GiftSelectionSession> | undefined => {
        const existingSession = giftSelectionStoreActions.getMostRecentSession(entityId, entityType);
        if (existingSession) {
            return giftSelectionStore$.sessions[existingSession.sessionId];
        }
        return undefined;
    },

    /**
     * Get active session for an entity
     * @param entityId - Entity identifier
     * @param entityType - Type of entity
     * @returns Active session or undefined
     */
    getActiveSession: (
        entityId: string,
        entityType: GiftSelectionEntityType
    ): Readonly<GiftSelectionSession> | undefined => {
        const session = giftSelectionStoreActions.getActiveSession(entityId, entityType);
        return session ? Object.freeze({ ...session }) : undefined;
    },

    /**
     * Get active session ID for an entity
     * @param entityId - Entity identifier
     * @param entityType - Type of entity
     * @returns Active session ID or undefined
     */
    getActiveSessionId: (entityId: string, entityType: GiftSelectionEntityType): string | undefined => {
        return giftSelectionStoreActions.getActiveSessionId(entityId, entityType);
    },
} as const;

// ============================================================================
// COMPUTED VALUES - READ-ONLY ACCESS
// ============================================================================

export const giftSelectionStoreComputedAPI = {
    /**
     * Check if session exists
     * @param sessionId - Session identifier
     * @returns True if session exists
     */
    sessionExists: (sessionId: string): boolean => {
        return giftSelectionStoreActions.getSessionById(sessionId) !== undefined;
    },

    /**
     * Get total number of sessions
     * @returns Count of all sessions
     */
    getTotalSessionsCount: (): number => {
        return Object.keys(giftSelectionStoreActions.getAllSessions()).length;
    },

    /**
     * Get number of turns in a session
     * @param sessionId - Session identifier
     * @returns Number of turns
     */
    getTurnsCount: (sessionId: string): number => {
        return giftSelectionStoreActions.getSessionTurns(sessionId).length;
    },

    /**
     * Check if session can add more turns
     * @param sessionId - Session identifier
     * @returns True if more turns can be added
     */
    canAddMoreTurns: (sessionId: string): boolean => {
        const session = giftSelectionStoreActions.getSessionById(sessionId);
        if (!session) return false;

        const currentTurns = session.selectionTurns?.length || 0;
        const maxTurns = session.maxSelectionTurns || 1;

        return currentTurns < maxTurns;
    },

    /**
     * Get remaining quantity for a session
     * @param sessionId - Session identifier
     * @param upToRound - Calculate remaining up to this round
     * @returns Remaining product quantity
     */
    getRemainingQuantity: (sessionId: string, upToRound: number = 1): number => {
        return giftSelectionStoreActions.getRemainingQuantity(sessionId, upToRound);
    },

    /**
     * Find turn by ID within a session
     * @param sessionId - Session identifier
     * @param turnId - Turn identifier
     * @returns Turn data or undefined
     */
    findTurnById: (sessionId: string, turnId: string): Readonly<GiftSelectionTurn> | undefined => {
        const turns = giftSelectionStoreActions.getSessionTurns(sessionId);
        const turn = turns.find(t => t.turnId === turnId);
        return turn ? Object.freeze({ ...turn }) : undefined;
    },

    /**
     * Check if entity has any sessions
     * @param entityId - Entity identifier
     * @param entityType - Type of entity
     * @returns True if entity has sessions
     */
    hasEntitySessions: (entityId: string, entityType: GiftSelectionEntityType): boolean => {
        return giftSelectionStoreActions.getSessionsByEntity(entityId, entityType).length > 0;
    },

    /**
     * Get all selected gifts across all turns in a session
     * @param sessionId - Session identifier
     * @returns Array of selected gifts
     */
    getAllSelectedGifts: (sessionId: string): ReadonlyArray<Readonly<GiftSelectionTurn['selectedGift']>> => {
        const turns = giftSelectionStoreActions.getSessionTurns(sessionId);
        return Object.freeze(turns.map(turn => Object.freeze({ ...turn.selectedGift })));
    },
} as const;

// ============================================================================
// MUTATIONS - CONTROLLED ACCESS
// ============================================================================

export const giftSelectionStoreMutationAPI = {
    /**
     * Create new gift selection session
     * @param entityId - Entity identifier
     * @param entityType - Type of entity
     * @param availableGifts - Available gift rules
     * @param maxTurns - Maximum turns allowed
     * @param productQuantity - Product quantity
     * @returns Session ID
     */
    createSession: (
        entityId: string,
        entityType: GiftSelectionEntityType,
        availableGifts: FreeGiftsData[],
        maxTurns?: number,
        productQuantity?: number
    ): string => {
        return giftSelectionStoreActions.createSession(entityId, entityType, availableGifts, maxTurns, productQuantity);
    },

    /**
     * Restore a session from server-synced data using the server-provided sessionId.
     * Creates a minimal session entry so the gift selection UI can bind to it.
     * No-op if the session already exists.
     *
     * @param serverSessionId - The session ID from the server (logic.sessionId)
     * @param entityId - Entity identifier (productId)
     * @param entityType - Type of entity ('product' or 'cart')
     * @param productQuantity - Product quantity
     */
    restoreServerSession: (
        serverSessionId: string,
        entityId: string,
        entityType: GiftSelectionEntityType,
        productQuantity?: number
    ): void => {
        giftSelectionStoreActions.restoreServerSession(serverSessionId, entityId, entityType, productQuantity);
    },

    /**
     * Update session data
     * @param sessionId - Session identifier
     * @param updates - Partial session updates
     */
    updateSession: (sessionId: string, updates: Partial<GiftSelectionSession>): void => {
        giftSelectionStoreActions.updateSession(sessionId, updates);
    },

    /**
     * Add turn to session
     * @param sessionId - Session identifier
     * @param turn - Turn data
     */
    addTurn: (sessionId: string, turn: GiftSelectionTurn): void => {
        giftSelectionStoreActions.addTurn(sessionId, turn);
    },

    /**
     * Update specific turn
     * @param sessionId - Session identifier
     * @param turnId - Turn identifier
     * @param updates - Partial turn updates
     */
    updateTurn: (sessionId: string, turnId: string, updates: Partial<GiftSelectionTurn>): void => {
        giftSelectionStoreActions.updateTurn(sessionId, turnId, updates);
    },

    /**
     * Remove turn from session
     * @param sessionId - Session identifier
     * @param turnId - Turn identifier
     */
    removeTurn: (sessionId: string, turnId: string): void => {
        giftSelectionStoreActions.removeTurn(sessionId, turnId);
    },

    /**
     * Delete session
     * @param sessionId - Session identifier
     */
    deleteSession: (sessionId: string): void => {
        giftSelectionStoreActions.deleteSession(sessionId);
    },

    /**
     * Delete all sessions for an entity
     * @param entityId - Entity identifier
     * @param entityType - Type of entity
     */
    deleteEntitySessions: (entityId: string, entityType: GiftSelectionEntityType): void => {
        giftSelectionStoreActions.deleteEntitySessions(entityId, entityType);
    },

    /**
     * Reset all sessions (use with caution)
     */
    resetAllSessions: (): void => {
        giftSelectionStoreActions.resetAllSessions();
    },

    /**
     * Set popup open state
     * @param sessionId - Session identifier
     * @param isOpen - Open state
     */
    setPopupOpen: (sessionId: string, isOpen: boolean): void => {
        giftSelectionStoreActions.setPopupOpen(sessionId, isOpen);
    },

    /**
     * Set gift options modal for specific turn
     * @param sessionId - Session identifier
     * @param turnId - Turn identifier (undefined to close)
     */
    setShowGiftOptionsForTurn: (sessionId: string, turnId?: string): void => {
        giftSelectionStoreActions.setShowGiftOptionsForTurn(sessionId, turnId);
    },

    /**
     * Set selected gifts for a session
     * @param sessionId - Session identifier
     * @param gifts - Array of selected gifts
     */
    setSelectedGifts: (sessionId: string, gifts: SelectedGift[]): void => {
        giftSelectionStoreActions.setSelectedGifts(sessionId, gifts);
    },

    /**
     * Add or update a gift in the session
     * @param sessionId - Session identifier
     * @param gift - Gift to add or update
     */
    addOrUpdateGift: (sessionId: string, gift: SelectedGift): void => {
        giftSelectionStoreActions.addOrUpdateGift(sessionId, gift);
    },

    /**
     * Remove a gift by ID
     * @param sessionId - Session identifier
     * @param giftId - Gift ID to remove
     */
    removeGift: (sessionId: string, giftId: string): void => {
        giftSelectionStoreActions.removeGift(sessionId, giftId);
    },

    /**
     * Clear all selected gifts for a session
     * @param sessionId - Session identifier
     */
    clearSelectedGifts: (sessionId: string): void => {
        giftSelectionStoreActions.clearSelectedGifts(sessionId);
    },

    /**
     * Set active session for an entity
     * @param sessionId - Session identifier
     * @param entityId - Entity identifier
     * @param entityType - Type of entity
     */
    setActiveSession: (sessionId: string, entityId: string, entityType: GiftSelectionEntityType): void => {
        giftSelectionStoreActions.setActiveSession(sessionId, entityId, entityType);
    },

    /**
     * Clear active session for an entity
     * @param entityId - Entity identifier
     * @param entityType - Type of entity
     */
    clearActiveSession: (entityId: string, entityType: GiftSelectionEntityType): void => {
        giftSelectionStoreActions.clearActiveSession(entityId, entityType);
    },
} as const;

// ============================================================================
// UNIFIED PUBLIC API
// ============================================================================

/**
 * UNIFIED GIFT SELECTION STORE API
 *
 * This is the ONLY interface external code should use.
 *
 * FORBIDDEN:
 * - Direct import of giftSelectionSessions$ from store
 * - Direct import of giftSelectionStoreActions from actions
 * - Bypassing this public API layer
 *
 * USAGE:
 * ```typescript
 * import {giftSelectionStoreAPI} from './stores/simple-gift-selection-store.public-api';
 *
 * // Read operations
 * const session = giftSelectionStoreAPI.state.getSessionById('session-123');
 * const turns = giftSelectionStoreAPI.state.getSessionTurns('session-123');
 * const canAdd = giftSelectionStoreAPI.computed.canAddMoreTurns('session-123');
 *
 * // Write operations
 * const sessionId = giftSelectionStoreAPI.actions.createSession('product-123', 'product', gifts);
 * giftSelectionStoreAPI.actions.addTurn(sessionId, turn);
 * giftSelectionStoreAPI.actions.deleteSession(sessionId);
 * ```
 */
export const giftSelectionStoreAPI = {
    state: giftSelectionStoreStateAPI,
    computed: giftSelectionStoreComputedAPI,
    actions: giftSelectionStoreMutationAPI,
} as const;

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type GiftSelectionStoreStateAPI = typeof giftSelectionStoreStateAPI;
export type GiftSelectionStoreComputedAPI = typeof giftSelectionStoreComputedAPI;
export type GiftSelectionStoreMutationAPI = typeof giftSelectionStoreMutationAPI;
export type GiftSelectionStoreAPI = typeof giftSelectionStoreAPI;
